import "plugins/gesture-handler";
import React, { useEffect } from "react";
import { Platform } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import useCachedResources from "./hooks/useCachedResources";
import Navigation from "./navigation";
import "./plugins/IntlPolyfill";
// redux
import { initTenantSubdomainAndType } from "features/slices/tenantSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { Provider } from "react-redux";
import { store } from "./store";

// ui-library
import * as eva from "@eva-design/eva";
import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { ApplicationProvider } from "@ui-kitten/components";
import Toast from "react-native-toast-message";

// plugins
import "./plugins/initDayjs";
import "./plugins/localization";
import "./plugins/santry";

// others
import { isDev } from "config/env";
import { default as mapping } from "config/evaMapping.json";
import toastConfig from "config/toastConfig";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import "./styles/index.css";
import "./styles/tailwind.css";

// react query
import { QueryClientProvider } from "@tanstack/react-query";
import { setAuthMethod, tryLocalSignIn } from "features/slices/authSlice";
import { queryClient } from "plugins/queryClient";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";

import "@expo/metro-runtime";
import {
  getClubDataBySubdomain,
  getFacebookPixelIdBySubdomain,
  getRedditPixelIdBySubdomain,
  getTiktokPixelIdBySubdomain,
  getTrackingIdsBySubdomain,
  getTradeDeskIdBySubdomain,
  getTwitterPixelIdBySubdomain,
} from "clients/TenantTrackingIds";
import {
  loadFacebookPixel,
  loadGoogleAnalytics,
  loadRedditPixel,
  loadTikTokPixel,
  loadTradeDesk,
  loadTwitterPixel,
  trackPageView,
} from "utils/analytics";

if (process.env.NODE_ENV === "production") {
  console.log = () => {};
  console.error = () => {};
  console.debug = () => {};
}

export default function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ActionSheetProvider>
          <GestureHandlerRootView style={{ flex: 1 }}>
            <App />
          </GestureHandlerRootView>
        </ActionSheetProvider>
      </Provider>
    </QueryClientProvider>
  );
}

function App() {
  const isLoadingComplete = useCachedResources();
  const { theme, customTheme } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const { data: tenantBranding } = useFetchTenantBrandingQuery(
    Platform.OS === "web",
  );

  const customMapping = Platform.OS === "web" ? mapping : {};

  const initiAnalytics = (tenantSubdomain: string) => {
    if (isDev) return;
    const tenantTrackingIds = getTrackingIdsBySubdomain(tenantSubdomain);
    const clubData = getClubDataBySubdomain(tenantSubdomain);
    const clubParams = clubData
      ? { club_id: clubData.club_id, club_name: clubData.club_name }
      : {};

    // Load GA for all IDs, then fire initial pageview once GA is ready.
    // Club params are passed into tenant IDs so gtag("set") fires before gtag("config").
    Promise.all([
      loadGoogleAnalytics("G-CDD0GBSB3H"),
      ...tenantTrackingIds.map((id) => loadGoogleAnalytics(id, clubParams)),
    ]).then(() => {
      if (Platform.OS === "web") {
        trackPageView();
      }
    });
    loadFacebookPixel(getFacebookPixelIdBySubdomain(tenantSubdomain));
    loadTwitterPixel(getTwitterPixelIdBySubdomain(tenantSubdomain));
    loadTikTokPixel(getTiktokPixelIdBySubdomain(tenantSubdomain));
    loadTradeDesk(getTradeDeskIdBySubdomain(tenantSubdomain));
    loadRedditPixel(getRedditPixelIdBySubdomain(tenantSubdomain));
  };

  useEffect(() => {
    // workaround for https://github.com/react-navigation/react-navigation/issues/10398
    //                https://github.com/react-navigation/react-navigation/issues/10481
    // waiting for fix for this
    // the bug is that, when user press the first item on drawer/bottom-tab, which is 'Home' in our app,
    // the browser will execute history.go(-1),[note: history.go(-2) if user lands on page other than the home page] triggered by underlying logic of react-navigation
    if (Platform.OS === "web") {
      const params = new URLSearchParams(window.location.search);
      const authMethod = params.get("authMethod");
      if (authMethod) dispatch(setAuthMethod(authMethod));
      history.pushState(null, "", "");
      history.pushState(null, "", "");
    }
  }, []);

  useEffect(() => {
    if (!tenantBranding) return;
    dispatch(initTenantSubdomainAndType(tenantBranding));
    initiAnalytics(tenantBranding.subdomain);
  }, [tenantBranding]);

  useEffect(() => {
    dispatch(tryLocalSignIn());
  }, []);

  if (!isLoadingComplete || !tenantConfig) {
    return null;
  }

  if (Platform.OS === "web" && !tenantBranding) {
    return null;
  }

  return (
    <ApplicationProvider
      {...eva}
      theme={theme === "light" ? customTheme : eva.dark}
      customMapping={{ ...eva.mapping, ...customMapping }}
    >
      <SafeAreaProvider>
        <Navigation />
        <Toast config={toastConfig} />
      </SafeAreaProvider>
    </ApplicationProvider>
  );
}
